import { createId } from "@paralleldrive/cuid2";
import { z } from "zod";

/**
 * Server-generated identifier. We accept three formats in production data:
 *
 *   - CUID2 (24-char, `[a-z][a-z0-9]{23}`) — minted by `createId()` for new
 *     resume section items, the canonical post-2025 format.
 *   - CUID v1 (25-char, `c` + 24 alphanumeric) — used by Prisma's default
 *     `cuid()` for User.id, Secrets.id, Resume.id, etc. The whole DB is
 *     full of these; rejecting them breaks login and every entity GET.
 *   - UUID v4 (36-char with dashes) — legacy resumes seeded/imported
 *     pre-2026 store section item ids in this format.
 *
 * The previous schema used `z.string().cuid2()` which (per Zod's source)
 * matches just `/^[0-9a-z]+$/` — loose enough that it accidentally
 * passed CUID v1 too. A stricter regex broke login. This explicit
 * union keeps the validation defensive but tolerant of every id format
 * actually present in our data. Defense-in-depth only — ids are
 * server-generated, not user-typed, no XSS risk.
 */
const cuid2Re = /^[a-z][\da-z]{23}$/;
const cuid1Re = /^c[\da-z]{24}$/;
const uuidRe = /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i;

export const idSchema = z
  .string()
  .min(1)
  .refine((value) => cuid2Re.test(value) || cuid1Re.test(value) || uuidRe.test(value), {
    message: "Invalid id — must be CUID v1/v2 or UUID v4",
  })
  .default(createId())
  .describe("Unique identifier — CUID v2 (preferred), CUID v1, or UUID v4");
